import React from 'react';
import { Helmet } from 'react-helmet-async';
import { siteConfig } from '../data/siteConfig';

export const WebsiteSchema = () => (
  <Helmet>
    <script type='application/ld+json'>
      {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: siteConfig.name,
        description: siteConfig.description,
        url: siteConfig.url,
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${siteConfig.url}/search?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      })}
    </script>
  </Helmet>
);

export const OrganizationSchema = () => (
  <Helmet>
    <script type='application/ld+json'>
      {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: siteConfig.businessInfo.name,
        url: siteConfig.url,
        logo: `${siteConfig.url}/images/gpm-logo.png`,
        description: siteConfig.description,
        foundingDate: siteConfig.businessInfo.foundingDate,
        address: {
          '@type': 'PostalAddress',
          streetAddress: siteConfig.businessInfo.address.streetAddress,
          addressLocality: siteConfig.businessInfo.address.addressLocality,
          addressRegion: siteConfig.businessInfo.address.addressRegion,
          addressCountry: siteConfig.businessInfo.address.addressCountry,
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: siteConfig.businessInfo.contactPoint.telephone,
          contactType: siteConfig.businessInfo.contactPoint.contactType,
          availableLanguage: siteConfig.businessInfo.contactPoint.availableLanguage,
        },
        sameAs: siteConfig.businessInfo.sameAs,
        serviceArea: {
          '@type': 'Place',
          name: 'United Kingdom',
        },
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Payroll Migration Services',
          itemListElement: [
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'SME Payroll Migration',
                description:
                  'Specialized payroll migration services for small and medium enterprises',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Large Enterprise Payroll Migration',
                description: 'Enterprise-grade payroll migration services for large corporations',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Payroll Migration Consultancy',
                description: 'Strategic consulting for complex payroll migration projects',
              },
            },
          ],
        },
      })}
    </script>
  </Helmet>
);

export const ServiceSchema = ({ service }) => (
  <Helmet>
    <script type='application/ld+json'>
      {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: service.name,
        description: service.description,
        provider: {
          '@type': 'Organization',
          name: siteConfig.businessInfo.name,
          url: siteConfig.url,
        },
        areaServed: {
          '@type': 'Country',
          name: 'United Kingdom',
        },
        serviceType: 'Payroll Migration',
        category: 'Business Services',
        offers: {
          '@type': 'Offer',
          description: service.description,
          seller: {
            '@type': 'Organization',
            name: siteConfig.businessInfo.name,
          },
        },
      })}
    </script>
  </Helmet>
);

export const BreadcrumbSchema = ({ items }) => (
  <Helmet>
    <script type='application/ld+json'>
      {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: `${siteConfig.url}${item.path}`,
        })),
      })}
    </script>
  </Helmet>
);

export const FAQSchema = ({ faqs }) => (
  <Helmet>
    <script type='application/ld+json'>
      {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      })}
    </script>
  </Helmet>
);

export const BlogSchema = ({ articles }) => (
  <Helmet>
    <script type='application/ld+json'>
      {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: `${siteConfig.name} Blog`,
        description: 'Expert insights on payroll migration and compliance',
        url: `${siteConfig.url}/blog`,
        publisher: {
          '@type': 'Organization',
          name: siteConfig.businessInfo.name,
          logo: {
            '@type': 'ImageObject',
            url: `${siteConfig.url}/images/gpm-logo.png`,
          },
        },
        blogPost: articles.map((article) => ({
          '@type': 'BlogPosting',
          headline: article.title,
          description: article.excerpt,
          url: `${siteConfig.url}/blog/${article.slug}`,
          datePublished: article.publishedDate,
          dateModified: article.modifiedDate || article.publishedDate,
          author: {
            '@type': 'Organization',
            name: siteConfig.businessInfo.name,
          },
        })),
      })}
    </script>
  </Helmet>
);
